import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Car } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: { bg: "bg-yellow-500", label: "Në Pritje" },
  confirmed: { bg: "bg-blue-500", label: "Konfirmuar" },
  active: { bg: "bg-green-500", label: "Aktive" },
  completed: { bg: "bg-gray-500", label: "Përfunduar" },
  cancelled: { bg: "bg-red-500", label: "Anulluar" },
  no_show: { bg: "bg-red-700", label: "Nuk u Paraqit" },
  overdue: { bg: "bg-orange-600", label: "Vonuar" },
};

const formatAlbanianDate = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  const days = ["E Diel", "E Hënë", "E Martë", "E Mërkurë", "E Enjte", "E Premte", "E Shtunë"];
  const months = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

export const AdminKalendari = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadCalendar(); }, [date]);

  const loadCalendar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/calendar?date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(res.data.bookings || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const changeDate = (delta) => {
    const d = new Date(date + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const goToday = () => setDate(new Date().toISOString().slice(0, 10));
  const isToday = date === new Date().toISOString().slice(0, 10);

  const toAbsoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Kalendari i Rezervimeve</h2>

      <div className="flex items-center justify-between bg-zinc-900 border border-gray-800 rounded-xl p-4 mb-6">
        <Button variant="ghost" onClick={() => changeDate(-1)} className="text-gray-400 hover:text-white" data-testid="cal-prev">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="text-center cursor-pointer" onClick={goToday}>
          <p className="text-yellow-500 font-bold text-lg" data-testid="cal-date">{formatAlbanianDate(date)}</p>
          {isToday && <p className="text-yellow-600 text-xs font-semibold uppercase tracking-wider">SOT</p>}
        </div>
        <Button variant="ghost" onClick={() => changeDate(1)} className="text-gray-400 hover:text-white" data-testid="cal-next">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">
          <div className="h-8 w-8 mx-auto mb-3 animate-spin rounded-full border-2 border-yellow-500 border-r-transparent" />
          <p>Po ngarkohet...</p>
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900 border border-gray-800 rounded-xl">
          <Car className="h-12 w-12 mx-auto mb-3 text-gray-700" />
          <p className="text-gray-500">Nuk ka rezervime për këtë datë</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => {
            const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
            return (
              <div key={b.id} className="bg-zinc-900 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors" data-testid={`cal-booking-${b.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-12 rounded-full ${sc.bg}`} />
                  {b.car_image && (
                    <div className="w-16 h-12 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                      <img src={toAbsoluteUrl(b.car_image)} alt="" className="w-full h-full object-contain" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-white font-semibold">{b.car_name}</p>
                    <p className="text-gray-400 text-sm">{b.customer_name}</p>
                    <p className="text-gray-500 text-xs">
                      {b.pickup_time || ""} - {b.return_time || ""} | {b.pickup_date} → {b.return_date}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${sc.bg}`}>
                    {sc.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 bg-zinc-900 border border-gray-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-4">
          {Object.entries(STATUS_COLORS).map(([key, { bg, label }]) => (
            <div key={key} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-sm ${bg}`} />
              <span className="text-gray-400 text-sm">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
